import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { Formik, Form } from 'formik';

import type { WidgetDropdownType } from 'views/components/widgets/Types';
import { Modal } from 'components/bootstrap';
import FormikInput from 'components/common/FormikInput';
import ModalSubmit from 'components/common/ModalSubmit';
import useViewsDispatch from 'views/stores/useViewsDispatch';
import { updateWidget } from 'views/logic/slices/widgetActions';
import SaveContextInfo from 'ai/common/SaveContextInfo';

type FormState = {
  context: string;
};
const AddContextActionComponent: WidgetDropdownType['component'] = ({ onClose, widget }) => {
  const dispatch = useViewsDispatch();
  const initialValues: FormState = useMemo(() => ({ context: widget?.context }), [widget?.context]);
  const onSubmit = useCallback(
    async ({ context }: FormState) => {
      const updatedWidget = widget.toBuilder().context(context).build();
      await dispatch(updateWidget(widget.id, updatedWidget));
      onClose();
    },
    [dispatch, onClose, widget],
  );

  return (
    <Modal show onHide={onClose}>
      <Modal.Header>
        <Modal.Title>Add/Edit Context</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Adding context clarifies your widget&#39;s purpose and helps users understand how to interpret its results.
          This also ensures the AI widget summary is generated with the most accurate and insightful analysis.
        </p>
        <Formik<FormState> initialValues={initialValues} onSubmit={onSubmit}>
          <Form>
            <FormikInput
              data-autofocus
              autoFocus
              label="Context"
              name="context"
              type="textarea"
              id="context"
              rows={12}
            />
            <SaveContextInfo />
            <ModalSubmit submitButtonText="Update" onCancel={onClose} />
          </Form>
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

const AddContextAction: WidgetDropdownType = {
  type: 'add-context-action',
  position: 'dropdown',
  component: AddContextActionComponent,
  title: () => <>Add/Edit Context</>,
};

export default AddContextAction;
